import { useEffect, useRef, useCallback } from 'react';
import type { LucideIcon } from 'lucide-react';

export interface ContextMenuItem {
  label: string;
  icon?: LucideIcon;
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
  separator?: boolean;
  /**
   * Marks the item as the featured/primary action. The context menu ignores
   * this flag (renders like any other row), but ActionPanel promotes the item
   * to a large filled card at the top. Use for stateful toggles like
   * Connect/Disconnect or Start/Stop.
   */
  primary?: boolean;
  /** Optional sublabel shown under the primary action's label. */
  sublabel?: string;
  /**
   * Only meaningful on `separator: true` items. When set, the ActionPanel
   * uses this as the small-caps title for the group that FOLLOWS this
   * separator. The ContextMenu ignores it (just renders a hairline).
   */
  sectionTitle?: string;
}

interface ContextMenuProps {
  items: ContextMenuItem[];
  position: { x: number; y: number };
  onClose: () => void;
}

export function ContextMenu({ items, position, onClose }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Compute position that stays within viewport
  const getAdjustedPosition = useCallback(() => {
    const menu = menuRef.current;
    if (!menu) return { x: position.x, y: position.y };

    const rect = menu.getBoundingClientRect();
    let { x, y } = position;

    if (x + rect.width > window.innerWidth) {
      x = window.innerWidth - rect.width - 4;
    }
    if (y + rect.height > window.innerHeight) {
      y = window.innerHeight - rect.height - 4;
    }
    if (x < 0) x = 4;
    if (y < 0) y = 4;

    return { x, y };
  }, [position]);

  // Close on mousedown outside or Escape
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Adjust position after render to flip if near edge
  useEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;

    const { x, y } = getAdjustedPosition();
    menu.style.left = `${x}px`;
    menu.style.top = `${y}px`;
  }, [getAdjustedPosition]);

  const handleItemClick = useCallback(
    (item: ContextMenuItem) => {
      if (item.disabled) return;
      item.onClick?.();
      onClose();
    },
    [onClose],
  );

  return (
    <div
      ref={menuRef}
      className="fixed bg-dbgo-panel border rounded-md shadow-lg min-w-[180px] py-1 z-50 animate-fade-in"
      style={{ left: position.x, top: position.y }}
    >
      {items.map((item, i) => {
        if (item.separator) {
          return <div key={i} className="border-t border-subtle my-1" />;
        }

        const Icon = item.icon;

        return (
          <button
            key={i}
            onClick={() => handleItemClick(item)}
            disabled={item.disabled}
            className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${
              item.disabled
                ? 'opacity-40 cursor-default'
                : item.danger
                  ? 'text-accent-red hover:bg-dbgo-tab-hover cursor-pointer'
                  : 'text-text-primary hover:bg-dbgo-tab-hover cursor-pointer'
            }`}
          >
            {Icon && <Icon size={14} className="flex-shrink-0" />}
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
